import { Builder } from '@builder.io/react';
import cn from 'classnames';
import { FC } from 'react';

import { COLOR_NAME_WHITE } from '@/shared/constants/colors';
import {
  actionButtonInput,
  backgroundColorInput,
  componentLinkIdInput,
} from '@/shared/inputs';
import { getBuilderSpacingClass } from '@/shared/spacing';
import { SharedBuilderComponent } from '@/shared/types';
import { getBuilderIOClassName } from '@/shared/utils/getBuilderIOClassName';

import { ActionVariant, BaseAction } from '../Action';
import { ComponentHeader } from '../ComponentHeader/ComponentHeader';
import sharedStyle from '../shared.module.scss';
import { LinkInput } from './Card';
import { Grid } from './Grid';

interface BuilderInput extends SharedBuilderComponent {
  componentLinkId?: string;
  title?: string;
  description?: string;
  action: BaseAction;
  actionVariant?: ActionVariant;
  links: LinkInput[];
}

type Props = BuilderInput & {
  locale: string;
};

export const AnchorLinks: FC<Props> = ({
  componentLinkId,
  title,
  description,
  action,
  actionVariant = ActionVariant.NONE,
  links = [],
  backgroundColorClassName,
  builderBlock,
  builderState,
  locale,
  attributes,
}) => {
  const builderStyles = getBuilderIOClassName(attributes);
  const spacingStyles = getBuilderSpacingClass(builderBlock, builderState);

  // When open page in editor or LivePreview mode, Builder doesn't run dataFetcher
  // causing locale value undefined. So, we use builderState locale value when
  // using component in editor and leave the locale when we generate static content
  const blockLocale = locale || builderState?.context.locale || 'en';

  return (
    <section
      data-testid="anchor-links"
      {...attributes}
      id={componentLinkId}
      className={cn(
        sharedStyle.section,
        builderStyles,
        spacingStyles,
        backgroundColorClassName
      )}
    >
      <ComponentHeader
        title={title}
        description={description}
        action={action}
        actionVariant={actionVariant}
        locale={blockLocale}
        headerClassName={cn('u-wrapper', {
          'u-m-b-xl u-m-b-lg-xxl-6': links && links.length > 0,
        })}
      >
        <Grid locale={blockLocale} links={links} />
      </ComponentHeader>
    </section>
  );
};

export function dataFetcher(locale: string) {
  return async () => ({
    locale,
  });
}

export const NAME = 'AnchorLinks';

export function componentRegister() {
  Builder.registerComponent(AnchorLinks, {
    name: NAME,
    inputs: [
      componentLinkIdInput(),
      {
        name: 'title',
        type: 'CatawikiEditor',
        defaultValue:
          'Section <span class="u-typography-h2-serif u-color-brand">Title</span>',
      },
      {
        name: 'description',
        type: 'CatawikiEditor',
        defaultValue:
          'Section description indicating the content to be foundand presented here with a medium legth copy block.',
      },
      ...actionButtonInput({
        allowVariants: [ActionVariant.NONE, ActionVariant.TERTIARY],
      }),
      backgroundColorInput(COLOR_NAME_WHITE),
      {
        name: 'links',
        type: 'list',
        friendlyName: 'Links',
        subFields: [
          {
            name: 'link',
            friendlyName: 'Link',
            type: 'CatawikiPlainEditor',
            defaultValue: '',
          },
          {
            name: 'linkType',
            friendlyName: 'Link Type',
            type: 'string',
            defaultValue: 'in the page',
            enum: ['in the page', 'external'],
          },
          {
            name: 'title',
            friendlyName: 'Title',
            type: 'CatawikiEditor',
            defaultValue: 'Section Title',
          },
          {
            name: 'subTitle',
            friendlyName: 'Sub Title',
            type: 'CatawikiEditor',
            defaultValue: 'Explore',
          },
          {
            name: 'imageUrl',
            friendlyName: 'Image File',
            type: 'file',
            allowedFileTypes: ['jpeg', 'png'],
          },
        ],
      },
    ],
    noWrap: true,
  });

  const v2Name = `v2:${NAME}`;
  Builder.registerComponent(AnchorLinks, {
    name: v2Name,
    inputs: [
      componentLinkIdInput(),
      {
        name: 'title',
        type: 'html',
        defaultValue:
          'Section <span class="u-typography-h2-serif u-color-brand">Title</span>',
      },
      {
        name: 'description',
        type: 'html',
        defaultValue:
          'Section description indicating the content to be foundand presented here with a medium legth copy block.',
      },
      ...actionButtonInput({
        allowVariants: [ActionVariant.NONE, ActionVariant.TERTIARY],
      }),
      backgroundColorInput(COLOR_NAME_WHITE),
      {
        name: 'links',
        type: 'list',
        friendlyName: 'Links',
        subFields: [
          {
            name: 'link',
            friendlyName: 'Link',
            type: 'CatawikiPlainEditorWithoutBuiltInTranslation',
            defaultValue: '',
          },
          {
            name: 'linkType',
            friendlyName: 'Link Type',
            type: 'string',
            defaultValue: 'in the page',
            enum: ['in the page', 'external'],
          },
          {
            name: 'title',
            friendlyName: 'Title',
            type: 'CatawikiEditorWithoutBuiltInTranslation',
            defaultValue: 'Section Title',
          },
          {
            name: 'subTitle',
            friendlyName: 'Sub Title',
            type: 'CatawikiEditorWithoutBuiltInTranslation',
            defaultValue: 'Explore',
          },
          {
            name: 'imageUrl',
            friendlyName: 'Image File',
            type: 'file',
            allowedFileTypes: ['jpeg', 'png'],
          },
        ],
      },
    ],
    noWrap: true,
  });

  return NAME;
}

export const componentDataFetchers = (locale: string) => ({
  [NAME]: dataFetcher(locale),
  [`v2:${NAME}`]: dataFetcher(locale),
});
